import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DASHBOARD_CONFERENCE_HISTORY,
  DASHBOARD_STATCREW,
  DASHBOARD_STATCREW_REVIEW,
  STATCREW_UPLOAD_CONFIRM,
  STATCREW_UPLOAD_GAME_XML,
  STATCREW_UPLOAD_MAP_TEAM_IDENTITY,
  STATCREW_VALIDATE_ZIP,
  STATCREW_VALIDATE_SCHEDULE,
  STATCREW_VALIDATE_ROSTER,
  getJobLifecycleUrl,
  getUploadScheduleUrl,
  getUploadRosterUrl,
} from '../constants/routes';
import AuthService from '../services/authservices';
import TeamIdentityMappingModal from './TeamIdentityMappingModal';
import UploadAcknowledgementModal from './UploadAcknowledgementModal';
import {
  ConferenceLookupResponse,
  MISSING_CONFERENCE_LABEL,
  fetchConferenceForSeason,
} from '../services/conferenceLookupService';
import { formatMatchTypeForDisplay } from '../utils/statcrewArtifactPreview';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ScheduleRow {
  id: string;
  date: string;
  opponent: string;
  ourScore: string;
  opponentScore: string;
  matchType: string;
}

interface RosterRow {
  id: string;
  classYear: string;
  position: string;
  firstName: string;
  familyName: string;
}

interface ScheduleSoftFilterSummary {
  matched_count?: number;
  total_schedule_rows?: number;
  keepers_remaining?: number;
  warnings?: unknown[];
}

type ApiPayload = Record<string, unknown>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const BATCH_SIZE = 20;
const authService = new AuthService();

function makeEmptyScheduleRows(count = BATCH_SIZE): ScheduleRow[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `${Date.now()}-${i}-${Math.random()}`,
    date: '',
    opponent: '',
    ourScore: '',
    opponentScore: '',
    matchType: '',
  }));
}

function makeEmptyRosterRows(count = BATCH_SIZE): RosterRow[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `${Date.now()}-${i}-${Math.random()}`,
    classYear: '',
    position: '',
    firstName: '',
    familyName: '',
  }));
}

function isApiPayload(value: unknown): value is ApiPayload {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function pickFirst(record: ApiPayload, keys: string[]): unknown {
  for (const key of keys) {
    const value = record[key];
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function extractArray(result: unknown, keys: string[]): unknown[] {
  if (!isApiPayload(result)) return [];

  for (const key of keys) {
    const value = result[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  const nestedData = result.data;
  if (!isApiPayload(nestedData)) return [];

  for (const key of keys) {
    const value = nestedData[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
}

function extractSeason(result: unknown): string {
  if (!isApiPayload(result)) return '';

  const directSeason = pickFirst(result, ['season']);
  if (directSeason !== undefined) {
    return toText(directSeason);
  }

  const nestedData = result.data;
  if (!isApiPayload(nestedData)) return '';

  return toText(pickFirst(nestedData, ['season']));
}

function splitScore(rawScore: string): Pick<ScheduleRow, 'ourScore' | 'opponentScore'> {
  const match = rawScore.match(/(-?\d+)\s*[-:–]\s*(-?\d+)/);
  if (!match) {
    return { ourScore: '', opponentScore: '' };
  }

  return {
    ourScore: match[1],
    opponentScore: match[2],
  };
}

function normalizeScheduleDate(value: string): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const slashDateMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (slashDateMatch) {
    const [, month, day, rawYear] = slashDateMatch;
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  const year = String(parsedDate.getFullYear());
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeClassYear(value: string): string {
  const normalized = value.trim().toLowerCase();

  if (normalized === 'fr' || normalized === 'freshman' || normalized === 'first year') return 'FR';
  if (normalized === 'so' || normalized === 'sophomore' || normalized === 'second year') return 'SO';
  if (normalized === 'jr' || normalized === 'junior' || normalized === 'third year') return 'JR';
  if (normalized === 'sr' || normalized === 'senior' || normalized === 'fourth year') return 'SR';

  return value;
}

function normalizeScheduleRows(rows: unknown[]): ScheduleRow[] {
  const mappedRows = rows
    .map((row, index) => {
      if (!isApiPayload(row)) return null;

      const scoreParts = splitScore(toText(pickFirst(row, ['score', 'gameScore'])));
      const ourScore = toText(pickFirst(row, ['ourScore', 'teamScore', 'scoreFor', 'our_score', 'team_score'])) || scoreParts.ourScore;
      const opponentScore = toText(pickFirst(row, ['opponentScore', 'awayScore', 'scoreAgainst', 'opponent_score', 'away_score'])) || scoreParts.opponentScore;

      const mappedRow: ScheduleRow = {
        id: `${Date.now()}-schedule-${index}-${Math.random()}`,
        date: normalizeScheduleDate(toText(pickFirst(row, ['date', 'gameDate', 'matchDate', 'game_date']))),
        opponent: toText(pickFirst(row, ['opponent', 'opponentName', 'team', 'opponentTeam', 'opponent_name'])),
        ourScore,
        opponentScore,
        matchType: formatMatchTypeForDisplay(
          pickFirst(row, ['match_type', 'matchType', 'match_types'])
        ),
      };

      return isScheduleRowFilled(mappedRow) ? mappedRow : null;
    })
    .filter((row): row is ScheduleRow => row !== null);

  return mappedRows.length > 0
    ? [...mappedRows, ...makeEmptyScheduleRows(1)]
    : makeEmptyScheduleRows(BATCH_SIZE);
}

function normalizeRosterRows(rows: unknown[]): RosterRow[] {
  const mappedRows = rows
    .map((row, index) => {
      if (!isApiPayload(row)) return null;

      const mappedRow: RosterRow = {
        id: `${Date.now()}-roster-${index}-${Math.random()}`,
        classYear: normalizeClassYear(toText(pickFirst(row, ['classYear', 'class', 'year', 'class_year']))),
        position: toText(pickFirst(row, ['position', 'pos', 'playerPosition', 'player_position'])),
        firstName: toText(pickFirst(row, ['firstName', 'first', 'givenName', 'first_name'])),
        familyName: toText(pickFirst(row, ['familyName', 'family_name', 'lastName', 'surname', 'last', 'last_name'])),
      };

      return isRosterRowFilled(mappedRow) ? mappedRow : null;
    })
    .filter((row): row is RosterRow => row !== null);

  return mappedRows.length > 0
    ? [...mappedRows, ...makeEmptyRosterRows(1)]
    : makeEmptyRosterRows(BATCH_SIZE);
}

// A row is "filled" if at least the key identifying fields are non-empty
const isScheduleRowFilled = (r: ScheduleRow) =>
  r.date !== '' || r.opponent !== '' || r.ourScore !== '' || r.opponentScore !== '';

const isRosterRowFilled = (r: RosterRow) =>
  r.classYear !== '' || r.position !== '' || r.firstName !== '' || r.familyName !== '';

// ─── Icons ────────────────────────────────────────────────────────────────────

function CalendarIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ZipIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function CheckIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="h-8 w-8 animate-spin text-cyan-500" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

// ─── Inline Error Banner ──────────────────────────────────────────────────────

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
      <span className="text-rose-500 mt-0.5"><AlertIcon /></span>
      <p className="flex-1 text-sm font-medium text-rose-700">{message}</p>
      <button onClick={onDismiss} className="rounded-lg p-0.5 text-rose-400 hover:bg-rose-100 hover:text-rose-600 transition">
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function formatScheduleMatchLine(summary: ScheduleSoftFilterSummary | null | undefined): string | null {
  if (!summary) return null;
  const matched = summary.matched_count ?? summary.keepers_remaining;
  const total = summary.total_schedule_rows;
  if (matched == null && total == null) return null;
  if (matched != null && total != null) {
    return `${matched} of ${total} schedule games matched to XML`;
  }
  if (matched != null) {
    return `${matched} schedule games matched to XML`;
  }
  if (total != null) {
    return `${total} schedule games`;
  }
  return null;
}

function QueuedModal({
  isOpen,
  onClose,
  scheduleSummary,
}: {
  isOpen: boolean;
  onClose: () => void;
  scheduleSummary?: ScheduleSoftFilterSummary | null;
}) {
  if (!isOpen) return null;

  const scheduleLine = formatScheduleMatchLine(scheduleSummary);
  const warningCount = Array.isArray(scheduleSummary?.warnings)
    ? scheduleSummary!.warnings!.length
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl px-8 py-10 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sky-100">
          <CheckIcon className="h-7 w-7 text-sky-600" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-slate-900">Processing started</h2>
        <p className="mt-2 text-sm text-slate-600">
          Your upload is running in the background. We will notify you by email once review is
          ready.
        </p>
        {scheduleLine && (
          <p className="mt-3 text-sm font-medium text-slate-700">{scheduleLine}</p>
        )}
        {warningCount > 0 && (
          <p className="mt-1 text-sm text-amber-700">
            {warningCount} schedule warning{warningCount === 1 ? '' : 's'} — review when ready.
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mt-8 rounded-xl bg-sky-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 transition"
        >
          Back to StatCrew
        </button>
      </div>
    </div>
  );
}

// ─── Stepper ──────────────────────────────────────────────────────────────────

function Stepper({ currentStep, steps }: { currentStep: number; steps: { label: string; icon: () => React.ReactElement }[] }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isCompleted = stepNumber < currentStep;
        const isCurrent = stepNumber === currentStep;
        return (
          <React.Fragment key={index}>
            <div className="flex items-center gap-2">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all ${
                isCompleted ? 'border-emerald-500 bg-emerald-500 text-white'
                  : isCurrent ? 'border-cyan-500 bg-white text-cyan-500'
                  : 'border-slate-200 bg-white text-slate-300'
              }`}>
                {isCompleted ? <CheckIcon /> : isCurrent ? step.icon() : <span className="text-sm font-semibold">{stepNumber}</span>}
              </div>
              <span className={`text-sm font-medium ${isCurrent ? 'text-slate-900' : isCompleted ? 'text-emerald-600' : 'text-slate-400'}`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`h-0.5 w-12 transition-all ${stepNumber < currentStep ? 'bg-emerald-500' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── CSV Popup ────────────────────────────────────────────────────────────────

function CsvPopup({ isOpen, onClose, onUpload }: { isOpen: boolean; onClose: () => void; onUpload: (file: File) => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Upload CSV File</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><XIcon className="h-5 w-5" /></button>
        </div>
        <div onClick={() => fileInputRef.current?.click()} className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 cursor-pointer hover:border-cyan-300 hover:bg-cyan-50/50 transition">
          <UploadIcon />
          <p className="mt-2 text-sm font-medium text-slate-600">{selectedFile ? selectedFile.name : 'Click to browse CSV file'}</p>
          <p className="text-xs text-slate-400">.csv files only</p>
        </div>
        <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) setSelectedFile(f); }} />
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={() => { if (selectedFile) { onUpload(selectedFile); setSelectedFile(null); onClose(); } }} disabled={!selectedFile} className={`rounded-xl px-4 py-2 text-sm font-medium transition ${selectedFile ? 'bg-cyan-500 text-white hover:bg-cyan-600' : 'cursor-not-allowed bg-slate-200 text-slate-400'}`}>Upload</button>
        </div>
      </div>
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────

function PreviewModal({ isOpen, onClose, title, subtitle, isLoading, children }: {
  isOpen: boolean; onClose: () => void; title: string; subtitle?: string; isLoading: boolean; children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} disabled={isLoading} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition disabled:opacity-30 disabled:cursor-not-allowed">
            <XIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner />
              <p className="text-sm text-slate-500">Validating with server…</p>
            </div>
          ) : children}
        </div>
        {!isLoading && (
          <div className="flex justify-end border-t border-slate-100 px-6 py-4 shrink-0">
            <button onClick={onClose} className="rounded-xl bg-cyan-500 px-6 py-2 text-sm font-semibold text-white hover:bg-cyan-600 transition">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Error Modal ──────────────────────────────────────────────────────────────

interface ApiError {
  title: string;
  message: string;
  endpoint?: string;
  showEndpoint?: boolean;
  errors?: string[];
  statusCode?: number;
}

function ErrorModal({ isOpen, onClose, error }: { isOpen: boolean; onClose: () => void; error: ApiError | null }) {
  if (!isOpen || !error) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center gap-3 border-b border-rose-100 px-6 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100">
            <AlertIcon />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-rose-900">{error.title}</h3>
            {error.statusCode && <p className="text-xs text-rose-600 mt-0.5">Error {error.statusCode}</p>}
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-100 hover:text-rose-600 transition">
            <XIcon className="h-5 w-5" />
          </button>
        </div>
        
        <div className="px-6 py-4">
          <p className="text-sm text-slate-700 mb-3">{error.message}</p>
          
          {error.showEndpoint && error.endpoint && (
            <div className="mb-3 rounded-lg bg-slate-50 p-2">
              <p className="text-xs font-mono text-slate-600 break-all">{error.endpoint}</p>
            </div>
          )}
          
          {error.errors && error.errors.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">Details:</p>
              <ul className="text-xs text-slate-600 space-y-1">
                {error.errors.map((err, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="text-rose-500">•</span>
                    <span>{err}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button onClick={onClose} className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 transition">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Row Counter Badge ────────────────────────────────────────────────────────

function RowCountBadge({ filled, total }: { filled: number; total: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
      {filled} / {total} rows filled
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function StatCrewUploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const username = searchParams.get('username') ?? '';
  const selectedSport = searchParams.get('selectedSport') ?? '';
  const initialSeason = searchParams.get('season') ?? '';
  const jobIdFromQuery = searchParams.get('jobId') ?? searchParams.get('job_id') ?? '';
  // Backend expects `college_name` for `/upload/init`. We source it from the auth payload
  // persisted by login (stored as `userInfo` in localStorage).
  const storedUser = authService.getStoredUser<any>();
  const collegeNameFromAuth = storedUser?.team?.name || storedUser?.team?.tidyName || '';

  const [currentStep, setCurrentStep] = useState(1);
  const [season, setSeason] = useState(initialSeason);
  const [showCsvPopup, setShowCsvPopup] = useState(false);
  const [initSummary, setInitSummary] = useState<{
    rosterTotal?: number;
    scheduleTotal?: number;
    xmlCount?: number;
    warnings?: unknown[];
  } | null>(null);

  /** Legacy upload steps removed — confirm runs automatically after init. */
  const SHOW_LEGACY_UPLOAD_STEPS = false;
  const [zipReadyForNext, setZipReadyForNext] = useState(false);
  const [scheduleReadyForNext, setScheduleReadyForNext] = useState(false);
  const [rosterReadyForSubmit, setRosterReadyForSubmit] = useState(false);

  // ── Step 1: ZIP ───────────────────────────────────────────────────────────
  const [xmlZip, setXmlZip] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [zipValid, setZipValid] = useState<boolean | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const [zipPreviewOpen, setZipPreviewOpen] = useState(false);
  const [zipPreviewLoading, setZipPreviewLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [queuedModalOpen, setQueuedModalOpen] = useState(false);
  const [queuedScheduleSummary, setQueuedScheduleSummary] = useState<ScheduleSoftFilterSummary | null>(null);
  const [teamIdentityModalOpen, setTeamIdentityModalOpen] = useState(false);
  const [teamIdentityCandidates, setTeamIdentityCandidates] = useState<any | null>(null);
  const [mappingJobId, setMappingJobId] = useState<string | null>(null);
  const [ackModalOpen, setAckModalOpen] = useState(false);
  const [ackPayload, setAckPayload] = useState<{
    jobId: string;
    eligible_xmls: string[];
    skipped_xmls: Array<{ filename: string; code?: string; message?: string }>;
    roster_total?: number;
    schedule_total?: number;
  } | null>(null);

  // ── Step 2: Schedule ──────────────────────────────────────────────────────
  const [scheduleData, setScheduleData] = useState<ScheduleRow[]>(makeEmptyScheduleRows(BATCH_SIZE));
  const [scheduleValid, setScheduleValid] = useState<boolean | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [schedulePreviewOpen, setSchedulePreviewOpen] = useState(false);
  const [schedulePreviewLoading, setSchedulePreviewLoading] = useState(false);
  const [scheduleSoftFilter, setScheduleSoftFilter] = useState<any | null>(null);
  const [scheduleXmlUploadBusyKey, setScheduleXmlUploadBusyKey] = useState<string | null>(null);
  const [seasonConference, setSeasonConference] = useState<ConferenceLookupResponse | null>(null);
  const [conferenceLoading, setConferenceLoading] = useState(false);

  // ── Step 3: Roster ────────────────────────────────────────────────────────
  const [rosterData, setRosterData] = useState<RosterRow[]>(makeEmptyRosterRows(BATCH_SIZE));
  const [rosterValid, setRosterValid] = useState<boolean | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [rosterPreviewOpen, setRosterPreviewOpen] = useState(false);
  const [rosterPreviewLoading, setRosterPreviewLoading] = useState(false);

  // ── Error Modal ───────────────────────────────────────────────────────────
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; error: ApiError | null }>({ isOpen: false, error: null });

  const dismissQueuedModal = useCallback(() => {
    setQueuedModalOpen(false);
    const params = new URLSearchParams();
    if (username) params.set('username', username);
    if (selectedSport) params.set('selectedSport', selectedSport);
    navigate(`${DASHBOARD_STATCREW}?${params.toString()}`);
  }, [navigate, selectedSport, username]);

  const steps = [{ label: 'Upload ZIP', icon: () => <ZipIcon /> }];

  const resetZipDerivedData = useCallback(() => {
    setScheduleData(makeEmptyScheduleRows(BATCH_SIZE));
    setScheduleValid(null);
    setScheduleError(null);
    setScheduleReadyForNext(false);
    setRosterData(makeEmptyRosterRows(BATCH_SIZE));
    setRosterValid(null);
    setRosterError(null);
    setRosterReadyForSubmit(false);
    setZipReadyForNext(false);
  }, []);

  const navigateToConferenceHistory = useCallback(() => {
    const params = new URLSearchParams();
    if (username) params.set('username', username);
    if (selectedSport) params.set('selectedSport', selectedSport);
    navigate(`${DASHBOARD_CONFERENCE_HISTORY}?${params.toString()}`);
  }, [navigate, selectedSport, username]);

  const loadSeasonConference = useCallback(async (
    seasonOverride?: string | number,
  ): Promise<ConferenceLookupResponse | null> => {
    const seasonKey = seasonOverride ?? season;
    if (!username || !selectedSport || !seasonKey) {
      setSeasonConference(null);
      return null;
    }
    setConferenceLoading(true);
    try {
      const result = await fetchConferenceForSeason({
        username,
        selectedSport,
        season: seasonKey,
      });
      setSeasonConference(result);
      return result;
    } catch {
      const fallback: ConferenceLookupResponse = {
        found: false,
        sportCode: selectedSport,
        season: Number(seasonKey) || 0,
        teamCode: null,
        teamName: collegeNameFromAuth || null,
        conferenceName: null,
        conferenceTidyName: null,
        displayLabel: null,
      };
      setSeasonConference(fallback);
      return fallback;
    } finally {
      setConferenceLoading(false);
    }
  }, [collegeNameFromAuth, season, selectedSport, username]);

  useEffect(() => {
    void loadSeasonConference();
  }, [loadSeasonConference]);

  useEffect(() => {
    if (currentStep === 2) {
      void loadSeasonConference();
    }
  }, [currentStep, loadSeasonConference]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadSeasonConference();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadSeasonConference]);

  // ── Error handler ──────────────────────────────────────────────────────────
  const showApiError = useCallback((title: string, error: unknown, endpoint?: string, statusCode?: number) => {
    let message = 'An unexpected error occurred';
    let errors: string[] = [];

    const prettifyDetail = (raw: string) => {
      const text = String(raw || '').trim();
      // Roster validation: "Missing required data (position or class) in Roster row 2"
      const rosterMatch = text.match(/Missing required data \\(([^)]+)\\) in Roster row (\\d+)/i);
      if (rosterMatch) {
        const fields = rosterMatch[1]
          .split(/\\s+or\\s+/i)
          .map((f) => f.trim())
          .filter(Boolean)
          .map((f) => f.charAt(0).toUpperCase() + f.slice(1));
        const rowNo = rosterMatch[2];
        return `Row ${rowNo}: ${fields.join(' and ')} ${fields.length > 1 ? 'are' : 'is'} required.`;
      }

      // Schedule validation: common row message
      const schedMatch = text.match(/Missing required data \\(([^)]+)\\) in Schedule row (\\d+)/i);
      if (schedMatch) {
        const fields = schedMatch[1]
          .split(/\\s+or\\s+/i)
          .map((f) => f.trim())
          .filter(Boolean)
          .map((f) => f.charAt(0).toUpperCase() + f.slice(1));
        const rowNo = schedMatch[2];
        return `Row ${rowNo}: ${fields.join(' and ')} ${fields.length > 1 ? 'are' : 'is'} required.`;
      }

      return text;
    };

    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'object' && error !== null) {
      const errObj = error as Record<string, unknown>;
      message = (errObj.message as string) || (errObj.error as string) || message;
      
      if (Array.isArray(errObj.errors)) {
        errors = errObj.errors.map(e => prettifyDetail(typeof e === 'string' ? e : String(e)));
      } else if (Array.isArray(errObj.details)) {
        errors = errObj.details.map(e => prettifyDetail(typeof e === 'string' ? e : String(e)));
      }
    }

    setErrorModal({
      isOpen: true,
      error: {
        title,
        message,
        endpoint: endpoint ?? undefined,
        showEndpoint: false,
        errors: errors.length > 0 ? errors : undefined,
        statusCode,
      },
    });
  }, []);

  // ── Schedule helpers ──────────────────────────────────────────────────────

  const addScheduleBatch = () =>
    setScheduleData(d => [...d, ...makeEmptyScheduleRows(1)]);

  const updateScheduleRow = useCallback((id: string, field: keyof ScheduleRow, value: string) => {
    setScheduleValid(null);
    setScheduleError(null);
    setScheduleReadyForNext(false);
    setScheduleData(d => d.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, []);

  // Only validate filled rows (ignore fully empty rows)
  const filledScheduleRows = scheduleData.filter(isScheduleRowFilled);

  // Transform for API: merge ourScore + opponentScore into a single "X-Y" score field
  const scheduleRowsForApi = filledScheduleRows.map(({ ourScore, opponentScore, matchType, ...rest }) => ({
    ...rest,
    score: `${ourScore}-${opponentScore}`,
    ...(matchType.trim() ? { match_type: matchType.trim() } : {}),
  }));

  const scheduleGamesForPut = scheduleRowsForApi.map(
    (g: { date: string; opponent: string; score: string; match_type?: string }) => ({
      date: g.date,
      opponent: g.opponent,
      score: g.score,
      ...(g.match_type ? { match_type: g.match_type } : {}),
    })
  );

  const scheduleFieldsComplete =
    !!season &&
    filledScheduleRows.length > 0 &&
    // Scores can be optional depending on backend validation flags.
    // Require only date + opponent for each filled row.
    filledScheduleRows.every(r => r.date && r.opponent);

  const conferenceLoaded = seasonConference !== null && !conferenceLoading;
  const conferenceConfigured = seasonConference?.found === true;
  const conferenceMissing = conferenceLoaded && !conferenceConfigured;
  const conferenceDisplayLabel = conferenceConfigured
    ? (seasonConference?.displayLabel || '')
    : conferenceLoading || !conferenceLoaded
      ? '—'
      : MISSING_CONFERENCE_LABEL;
  const canValidateSchedule = scheduleFieldsComplete && conferenceConfigured;

  const handleSchedulePreview = async () => {
    setSchedulePreviewOpen(true);
    setSchedulePreviewLoading(true);
    setScheduleError(null);
    setScheduleValid(null);
    setScheduleSoftFilter(null);

    try {
      const conference = await loadSeasonConference();
      if (!conference?.found) {
        setScheduleValid(false);
        setScheduleReadyForNext(false);
        setScheduleError(
          'No conference is configured for this season. Edit in Conference History, then try again.'
        );
        setSchedulePreviewLoading(false);
        return;
      }
      if (!jobId) {
        throw new Error('Job ID not available. Please upload ZIP again.');
      }
      // Persist current edits before validating (backend validates on-disk JSON).
      const schedulePutBody = { games: scheduleGamesForPut };
      const schedulePutRes = await authService.authenticatedFetch(getUploadScheduleUrl(jobId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(schedulePutBody),
      });
      if (!schedulePutRes.ok) {
        const err = await schedulePutRes.json().catch(() => ({}));
        setScheduleValid(false);
        setScheduleReadyForNext(false);
        setSchedulePreviewOpen(false);
        showApiError('Schedule Update Failed', err, getUploadScheduleUrl(jobId), schedulePutRes.status);
        return;
      }

      const formData = new FormData();
      formData.append('job_id', jobId);
      const res = await authService.authenticatedFetch(STATCREW_VALIDATE_SCHEDULE, {
        method: 'POST',
        body: formData,
      });
      
      if (res.ok) {
        const payload = await res.json().catch(() => ({}));
        const soft = payload?.data?.schedule_soft_filter ?? payload?.data?.scheduleSoftFilter ?? null;
        if (soft) setScheduleSoftFilter(soft);
        setScheduleValid(true);
        setScheduleReadyForNext(true);
      } else {
        const errData = await res.json().catch(() => ({}));
        const soft = errData?.data?.schedule_soft_filter ?? errData?.data?.scheduleSoftFilter ?? null;
        if (soft) setScheduleSoftFilter(soft);
        setScheduleValid(false);
        setScheduleReadyForNext(false);
        // Keep preview open so user can act on warnings (upload missing XML).
        setScheduleError(errData?.message || 'Schedule validation failed. Please review the warnings and fix issues.');
      }
    } catch (err) {
      setScheduleValid(false);
      setScheduleReadyForNext(false);
      setSchedulePreviewOpen(false);
      showApiError('Schedule Validation Error', err, STATCREW_VALIDATE_SCHEDULE);
    } finally {
      setSchedulePreviewLoading(false);
    }
  };

  const uploadMissingGameXml = async (args: { file: File; detail: any; key: string }) => {
    const { file, detail, key } = args;
    if (!jobId) {
      showApiError('XML Upload Failed', { message: 'Job ID not available. Please upload ZIP again.' }, STATCREW_UPLOAD_GAME_XML);
      return;
    }
    setScheduleXmlUploadBusyKey(key);
    try {
      const fd = new FormData();
      fd.append('job_id', jobId);
      fd.append('xml_file', file);
      fd.append('expected_date', String(detail?.date ?? ''));
      fd.append('expected_opponent', String(detail?.opponent ?? ''));
      fd.append('expected_score', String(detail?.score ?? ''));
      const res = await authService.authenticatedFetch(STATCREW_UPLOAD_GAME_XML, { method: 'POST', body: fd });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.success) {
        showApiError('XML Upload Failed', payload, STATCREW_UPLOAD_GAME_XML, res.status);
        return;
      }
      // Re-validate schedule so warnings/match count refresh after ZIP update.
      await handleSchedulePreview();
    } catch (e) {
      showApiError('XML Upload Error', e, STATCREW_UPLOAD_GAME_XML);
    } finally {
      setScheduleXmlUploadBusyKey(null);
    }
  };

  // ── Roster helpers ────────────────────────────────────────────────────────

  const addRosterBatch = () =>
    setRosterData(d => [...d, ...makeEmptyRosterRows(1)]);

  const updateRosterRow = useCallback((id: string, field: keyof RosterRow, value: string) => {
    setRosterValid(null);
    setRosterError(null);
    setRosterReadyForSubmit(false);
    setRosterData(d => d.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, []);

  const filledRosterRows = rosterData.filter(isRosterRowFilled);

  const rosterFieldsComplete =
    filledRosterRows.length > 0 &&
    // Position/class can be optional depending on backend validation flags.
    // Require names only for each filled row.
    filledRosterRows.every(r => r.firstName || r.familyName);

  const handleRosterPreview = async () => {
    setRosterPreviewOpen(true);
    setRosterPreviewLoading(true);
    setRosterError(null);
    setRosterValid(null);

    try {
      if (!jobId) {
        throw new Error('Job ID not available. Please upload ZIP again.');
      }
      // Persist current edits before validating (backend validates on-disk JSON).
      const rosterPutBody = {
        players: filledRosterRows.map(r => ({
          first_name: r.firstName,
          family_name: r.familyName,
          position: r.position,
          class: r.classYear,
        })),
      };
      const rosterPutRes = await authService.authenticatedFetch(getUploadRosterUrl(jobId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rosterPutBody),
      });
      if (!rosterPutRes.ok) {
        const err = await rosterPutRes.json().catch(() => ({}));
        setRosterValid(false);
        setRosterReadyForSubmit(false);
        setRosterPreviewOpen(false);
        showApiError('Roster Update Failed', err, getUploadRosterUrl(jobId), rosterPutRes.status);
        return;
      }

      const formData = new FormData();
      formData.append('job_id', jobId);
      const res = await authService.authenticatedFetch(STATCREW_VALIDATE_ROSTER, {
        method: 'POST',
        body: formData,
      });
      
      if (res.ok) {
        await res.json().catch(() => ({}));
        setRosterValid(true);
        setRosterReadyForSubmit(true);
      } else {
        const errData = await res.json().catch(() => ({}));
        setRosterValid(false);
        setRosterReadyForSubmit(false);
        setRosterPreviewOpen(false);
        showApiError('Roster Validation Failed', errData, STATCREW_VALIDATE_ROSTER, res.status);
      }
    } catch (err) {
      setRosterValid(false);
      setRosterReadyForSubmit(false);
      setRosterPreviewOpen(false);
      showApiError('Roster Validation Error', err, STATCREW_VALIDATE_ROSTER);
    } finally {
      setRosterPreviewLoading(false);
    }
  };

  // ── ZIP helpers ───────────────────────────────────────────────────────────

  const handleZipPreview = async () => {
    if (!xmlZip) return;
    setZipPreviewOpen(false);
    setZipPreviewLoading(true);
    setZipError(null);
    setZipValid(null);
    setZipReadyForNext(false);
    setInitSummary(null);
    setSubmitError(null);

    try {
      const formData = new FormData();
      if (!collegeNameFromAuth) {
        throw new Error('College name missing from auth data. Please login again.');
      }

      formData.append('xml_zip', xmlZip);
      formData.append('college_name', collegeNameFromAuth);
      formData.append('sport_code', selectedSport);
      formData.append('season', season);
      const teamId = storedUser?.team?.id;
      const teamCode = storedUser?.team?.code;
      if (teamId) formData.append('team_id', String(teamId));
      if (teamCode != null) formData.append('team_code', String(teamCode));
      if (collegeNameFromAuth) formData.append('team_name', collegeNameFromAuth);

      const res = await authService.authenticatedFetch(STATCREW_VALIDATE_ZIP, { method: 'POST', body: formData });
      const result = await res.json();
      if (res.ok && result.success) {
        if (result.data?.team_identity_unresolved) {
          setMappingJobId(String(result.data.job_id));
          setTeamIdentityCandidates(result.data.identity_candidates || result.data.candidates || null);
          setTeamIdentityModalOpen(true);
          setZipPreviewLoading(false);
          return;
        }

        const nextSeason = extractSeason(result.data);
        const nextScheduleRows = normalizeScheduleRows(extractArray(result.data.generated, ['schedule_json', 'scheduleJson', 'schedule']));
        const nextRosterRows = normalizeRosterRows(extractArray(result.data.generated, ['roster_json', 'rosterJson', 'roster']));
        const generated = (result.data?.generated || {}) as Record<string, unknown>;

        const resolvedSeason = nextSeason ?? (season ? Number(season) : null);
        if (nextSeason) setSeason(String(nextSeason));
        const newJobId = String(result.data.job_id);
        setJobId(newJobId);
        setScheduleData(nextScheduleRows);
        setRosterData(nextRosterRows);
        setZipValid(true);
        setZipReadyForNext(true);
        setInitSummary({
          rosterTotal: Number(generated.roster_total ?? nextRosterRows.length),
          scheduleTotal: Number(generated.schedule_total ?? nextScheduleRows.length),
          xmlCount: Number((generated as { xml_count?: number }).xml_count ?? 0),
          warnings: Array.isArray(generated.warnings) ? generated.warnings : [],
        });
        if (resolvedSeason != null) {
          await loadSeasonConference(resolvedSeason);
        }
        setAckPayload({
          jobId: newJobId,
          eligible_xmls: Array.isArray(generated.eligible_xmls) ? generated.eligible_xmls : [],
          skipped_xmls: Array.isArray(generated.skipped_xmls) ? generated.skipped_xmls : [],
          roster_total: Number(generated.roster_total ?? nextRosterRows.length),
          schedule_total: Number(generated.schedule_total ?? nextScheduleRows.length),
        });
        setAckModalOpen(true);
      } else {
        setZipValid(false);
        setZipReadyForNext(false);
        showApiError('ZIP Validation Failed', result, STATCREW_VALIDATE_ZIP, res.status);
      }
    } catch (err) {
      setZipValid(false);
      setZipReadyForNext(false);
      showApiError('ZIP Upload Error', err, STATCREW_VALIDATE_ZIP);
    } finally {
      setZipPreviewLoading(false);
    }
  };

  // ── Final submit / auto-confirm ───────────────────────────────────────────

  const confirmAndStartProcessing = useCallback(async (targetJobId: string) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const formData = new FormData();
      formData.append('job_id', targetJobId);
      const confirmRes = await authService.authenticatedFetch(STATCREW_UPLOAD_CONFIRM, {
        method: 'POST',
        body: formData,
      });
      if (!confirmRes.ok) {
        const err = await confirmRes.json().catch(() => ({}));
        showApiError('Upload Confirmation Failed', err, STATCREW_UPLOAD_CONFIRM, confirmRes.status);
        return false;
      }
      const result = await confirmRes.json();
      if (result.success) {
        const softFilter =
          result.data?.schedule_soft_filter ?? result.data?.scheduleSoftFilter ?? null;
        setQueuedScheduleSummary(softFilter);
        setQueuedModalOpen(true);
        return true;
      }
      showApiError('Upload Confirmation Failed', result, STATCREW_UPLOAD_CONFIRM, confirmRes.status);
      return false;
    } catch (err) {
      showApiError('Upload Error', err, STATCREW_UPLOAD_CONFIRM);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [showApiError]);

  const handleTeamIdentityMapping = useCallback(async (mapping: Record<string, { team_name: string; team_code: number }>) => {
    if (!mappingJobId) return;
    setIsSubmitting(true);
    try {
      const res = await authService.authenticatedFetch(
        `${STATCREW_UPLOAD_MAP_TEAM_IDENTITY}/${mappingJobId}/map-team-identity`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(mapping),
        },
      );
      const result = await res.json().catch(() => ({}));
      if (!res.ok || !result.success) {
        showApiError('Team Identity Mapping Failed', result, `${STATCREW_UPLOAD_MAP_TEAM_IDENTITY}/${mappingJobId}/map-team-identity`, res.status);
        return;
      }
      if (result.data?.team_identity_unresolved) {
        setTeamIdentityCandidates(result.data.identity_candidates || result.data.candidates || null);
        setTeamIdentityModalOpen(true);
        return;
      }
      setTeamIdentityModalOpen(false);
      setTeamIdentityCandidates(null);
      const newJobId = String(result.data.job_id);
      setJobId(newJobId);
      setZipValid(true);
      setZipReadyForNext(true);
      const generated = (result.data?.generated || {}) as Record<string, unknown>;
      setAckPayload({
        jobId: newJobId,
        eligible_xmls: Array.isArray(generated.eligible_xmls) ? generated.eligible_xmls : [],
        skipped_xmls: Array.isArray(generated.skipped_xmls) ? generated.skipped_xmls : [],
        roster_total: Number(generated.roster_total ?? 0),
        schedule_total: Number(generated.schedule_total ?? 0),
      });
      setAckModalOpen(true);
    } catch (err) {
      showApiError('Team Identity Mapping Error', err, `${STATCREW_UPLOAD_MAP_TEAM_IDENTITY}/${mappingJobId}/map-team-identity`);
    } finally {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingJobId, showApiError, confirmAndStartProcessing]);

  const handleFinalSubmit = async () => {
    if (!jobId) return;
    await confirmAndStartProcessing(jobId);
  };

  const handleAckProceed = useCallback(async () => {
    if (!ackPayload?.jobId) return;
    setAckModalOpen(false);
    await confirmAndStartProcessing(ackPayload.jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ackPayload]);

  const handleAckCancel = useCallback(() => {
    setAckModalOpen(false);
    setAckPayload(null);
    setJobId(null);
    setXmlZip(null);
    setZipValid(null);
    setZipPreviewOpen(false);
    setZipReadyForNext(false);
    setInitSummary(null);
  }, []);

  // Resume an in-flight job from history (?jobId=…)
  useEffect(() => {
    let isActive = true;

    async function hydrateFromExistingJob(existingJobId: string) {
      try {
        setJobId(existingJobId);

        const lifecycleRes = await authService.authenticatedFetch(getJobLifecycleUrl(existingJobId));
        const lifecycle = lifecycleRes.ok ? await lifecycleRes.json().catch(() => ({})) : {};

        if (lifecycle.status === 'READY_TO_REVIEW') {
          navigate(`${DASHBOARD_STATCREW_REVIEW}?jobId=${encodeURIComponent(existingJobId)}`);
          return;
        }

        if (lifecycle.awaiting_confirmation) {
          setZipValid(true);
          setZipReadyForNext(true);
          await confirmAndStartProcessing(existingJobId);
          return;
        }

        if (lifecycle.status === 'IN_PROGRESS' || lifecycle.status === 'UPLOADED') {
          setZipValid(true);
          setQueuedModalOpen(true);
          return;
        }
      } catch {
        if (!isActive) return;
        setCurrentStep(1);
      }
    }

    if (jobIdFromQuery) {
      void hydrateFromExistingJob(jobIdFromQuery);
    }

    return () => {
      isActive = false;
    };
  }, [jobIdFromQuery, confirmAndStartProcessing, navigate]);

  const zipSatisfied = zipValid === true || Boolean(jobId);
  const canSubmitFinal = zipSatisfied && Boolean(jobId) && !isSubmitting;

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">

      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-sky-500">{selectedSport}</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Upload Season ZIP</h1>
          <p className="mt-1 text-sm text-slate-500">Upload your StatCrew ZIP — roster and schedule are built automatically, then processing starts.</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition">
            ← Back
          </button>
        </div>
      </div>

      <Stepper currentStep={currentStep} steps={steps} />

      {/* ════════════════════════════════════════════════════════════════════
          STEP 1 — ZIP Upload
         ════════════════════════════════════════════════════════════════════ */}
      {currentStep === 1 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Upload StatCrew ZIP</h2>
              <p className="mt-1 text-sm text-slate-500">We generate roster and schedule from your XML, queue the worker, and open review when ready.</p>
            </div>
          </div>

          <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Season</p>
            <p className="mt-1 text-sm text-slate-700">{season || 'Will be filled from the ZIP response if available'}</p>
          </div>

          <div
            onClick={() => document.getElementById('zip-input')?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 transition ${
              xmlZip ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50/60 hover:border-sky-300 hover:bg-sky-50/40'
            }`}
          >
            {xmlZip ? (
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                  <CheckIcon className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-800">{xmlZip.name}</p>
                  <p className="text-xs text-emerald-600">{(xmlZip.size / (1024 * 1024)).toFixed(2)} MB</p>
                </div>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setXmlZip(null);
                    setZipValid(null);
                    setZipReadyForNext(false);
                    setZipError(null);
                    setSubmitError(null);
                    resetZipDerivedData();
                  }}
                  className="ml-4 rounded-lg p-2 text-emerald-600 hover:bg-emerald-100"
                >
                  <TrashIcon />
                </button>
              </div>
            ) : (
              <>
                <ZipIcon />
                <p className="mt-3 text-sm font-medium text-slate-600">Drop ZIP file here or click to browse</p>
                <p className="text-xs text-slate-400">Upload StatCrew XML files in ZIP format</p>
              </>
            )}
          </div>

          <input
            id="zip-input"
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (!file) return;

              setXmlZip(file);
              setZipValid(null);
              setZipReadyForNext(false);
              setZipError(null);
              setSubmitError(null);
              resetZipDerivedData();
            }}
          />

          {zipError && <ErrorBanner message={zipError} onDismiss={() => setZipError(null)} />}
          {submitError && <ErrorBanner message={submitError} onDismiss={() => setSubmitError(null)} />}

          <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 pt-6">
            <button
              onClick={handleZipPreview}
              disabled={!xmlZip || isSubmitting || zipPreviewLoading}
              title={!xmlZip ? 'Upload a ZIP file first' : undefined}
              className={`rounded-xl border px-6 py-2 text-sm font-semibold transition ${
                xmlZip && !isSubmitting && !zipPreviewLoading
                  ? 'border-cyan-500 bg-cyan-500 text-white hover:bg-cyan-600'
                  : 'cursor-not-allowed border-slate-100 bg-slate-100 text-slate-400'
              }`}
            >
              {zipPreviewLoading || isSubmitting ? 'Processing…' : 'Upload & start processing'}
            </button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          STEP 2 — Schedule
         ════════════════════════════════════════════════════════════════════ */}
      {SHOW_LEGACY_UPLOAD_STEPS && currentStep === 2 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Step 2: Schedule</h2>
            <RowCountBadge filled={filledScheduleRows.length} total={scheduleData.length} />
          </div>

          <div className="p-6">
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Season : {season}</label>
              {conferenceConfigured && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="text-slate-600">
                    Conference:{' '}
                    <span className="font-medium text-slate-800">{conferenceDisplayLabel}</span>
                  </span>
                  <button
                    type="button"
                    onClick={navigateToConferenceHistory}
                    className="text-xs font-semibold text-cyan-600 hover:text-cyan-700"
                  >
                    Edit in Conference History
                  </button>
                </div>
              )}
            </div>

            {conferenceMissing && (
              <div className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-900">
                  No conference is configured for this season. Schedule validation is blocked until you add one.
                </p>
                <button
                  type="button"
                  onClick={navigateToConferenceHistory}
                  className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                >
                  Edit in Conference History
                </button>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto max-h-[480px]">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr>
                      <th className="w-10 border-b border-slate-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-widest text-slate-400">#</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Date</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Opponent</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Conference</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Our Score</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Opp. Score</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Match type (optional)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleData.map((row, idx) => {
                      const filled = isScheduleRowFilled(row);
                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`group transition-colors ${filled ? 'bg-white' : 'bg-slate-50/40'} hover:bg-sky-50/40`}>
                            <td className="border-b border-slate-100 px-3 py-2 text-center text-xs text-slate-300 font-medium">{idx + 1}</td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="date"
                                value={row.date}
                                onChange={e => updateScheduleRow(row.id, 'date', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="text"
                                value={row.opponent}
                                placeholder="Opponent name"
                                onChange={e => updateScheduleRow(row.id, 'opponent', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <span className="block min-w-[140px] px-2 py-1.5 text-sm text-slate-500">
                                {filled ? conferenceDisplayLabel : '—'}
                              </span>
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="number"
                                value={row.ourScore}
                                placeholder="0"
                                onChange={e => updateScheduleRow(row.id, 'ourScore', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="number"
                                value={row.opponentScore}
                                placeholder="0"
                                onChange={e => updateScheduleRow(row.id, 'opponentScore', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="text"
                                value={row.matchType}
                                placeholder="e.g. Playoff, Bowl"
                                onChange={e => updateScheduleRow(row.id, 'matchType', e.target.value)}
                                className="w-full min-w-[160px] rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
                <span className="text-xs text-slate-400">{scheduleData.length} rows total</span>
                <button
                  onClick={addScheduleBatch}
                  className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-1.5 text-xs font-semibold text-slate-500 hover:border-sky-400 hover:bg-sky-50 hover:text-sky-600 transition"
                >
                  <PlusIcon />
                  Add 1 More Row
                </button>
              </div>
            </div>

            {scheduleError && <ErrorBanner message={scheduleError} onDismiss={() => setScheduleError(null)} />}

            <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-6">
              <button onClick={() => setCurrentStep(1)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                ← Previous
              </button>
              <div className="flex items-center gap-3">
                {!scheduleReadyForNext ? (
                  <button
                    type="button"
                    onClick={handleSchedulePreview}
                    disabled={!canValidateSchedule || conferenceLoading}
                    title={
                      !scheduleFieldsComplete
                        ? 'Fill in all fields and season to validate'
                        : conferenceLoading
                          ? 'Loading conference for this season…'
                          : conferenceMissing
                            ? 'Configure conference for this season in Conference History'
                            : undefined
                    }
                    className={`rounded-xl px-6 py-2 text-sm font-semibold transition ${
                      canValidateSchedule && !conferenceLoading
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                        : 'cursor-not-allowed bg-slate-200 text-slate-400'
                    }`}
                  >
                    Validate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    className="rounded-xl bg-cyan-500 px-6 py-2 text-sm font-semibold text-white transition hover:bg-cyan-600"
                  >
                    Next step →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          STEP 3 — Roster
         ════════════════════════════════════════════════════════════════════ */}
      {SHOW_LEGACY_UPLOAD_STEPS && currentStep === 3 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Step 3: Roster</h2>
            <RowCountBadge filled={filledRosterRows.length} total={rosterData.length} />
          </div>

          <div className="p-6">
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto max-h-[480px]">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr>
                      <th className="w-10 border-b border-slate-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-widest text-slate-400">#</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Class</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Position</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">First Name</th>
                      <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">Family Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rosterData.map((row, idx) => {
                      const filled = isRosterRowFilled(row);
                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`group transition-colors ${filled ? 'bg-white' : 'bg-slate-50/40'} hover:bg-sky-50/40`}>
                            <td className="border-b border-slate-100 px-3 py-2 text-center text-xs text-slate-300 font-medium">{idx + 1}</td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <select
                                value={row.classYear}
                                onChange={e => updateRosterRow(row.id, 'classYear', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              >
                                <option value="">—</option>
                                <option value="FR">Freshman</option>
                                <option value="SO">Sophomore</option>
                                <option value="JR">Junior</option>
                                <option value="SR">Senior</option>
                              </select>
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="text"
                                value={row.position}
                                placeholder="e.g. QB"
                                onChange={e => updateRosterRow(row.id, 'position', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="text"
                                value={row.firstName}
                                placeholder="First name"
                                onChange={e => updateRosterRow(row.id, 'firstName', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                            <td className="border-b border-slate-100 px-2 py-2">
                              <input
                                type="text"
                                value={row.familyName}
                                placeholder="Family name"
                                onChange={e => updateRosterRow(row.id, 'familyName', e.target.value)}
                                className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 group-hover:border-slate-200"
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
                <span className="text-xs text-slate-400">{rosterData.length} rows total</span>
                <button
                  onClick={addRosterBatch}
                  className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-1.5 text-xs font-semibold text-slate-500 hover:border-sky-400 hover:bg-sky-50 hover:text-sky-600 transition"
                >
                  <PlusIcon />
                  Add 1 More Row
                </button>
              </div>
            </div>

            {rosterError && <ErrorBanner message={rosterError} onDismiss={() => setRosterError(null)} />}
            {submitError && <ErrorBanner message={submitError} onDismiss={() => setSubmitError(null)} />}

            <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-6">
              <button onClick={() => setCurrentStep(2)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                ← Previous
              </button>
              <div className="flex items-center gap-3">
                {!rosterReadyForSubmit ? (
                  <button
                    type="button"
                    onClick={handleRosterPreview}
                    disabled={!rosterFieldsComplete}
                    title={!rosterFieldsComplete ? 'Fill in all fields to validate' : undefined}
                    className={`rounded-xl px-8 py-3 text-sm font-semibold transition ${
                      rosterFieldsComplete
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                        : 'cursor-not-allowed bg-slate-200 text-slate-400'
                    }`}
                  >
                    Validate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleFinalSubmit}
                    disabled={!canSubmitFinal}
                    title={
                      !canSubmitFinal
                        ? scheduleValid !== true
                          ? 'Validate schedule on the previous step first'
                          : !rosterReadyForSubmit
                            ? 'Validate roster first'
                            : undefined
                        : undefined
                    }
                    className={`rounded-xl px-8 py-3 text-sm font-semibold transition ${
                      canSubmitFinal ? 'bg-cyan-500 text-white hover:bg-cyan-600' : 'cursor-not-allowed bg-slate-200 text-slate-400'
                    }`}
                  >
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          PREVIEW MODALS
         ════════════════════════════════════════════════════════════════════ */}

      <PreviewModal isOpen={schedulePreviewOpen} onClose={() => setSchedulePreviewOpen(false)} title="Schedule Preview" subtitle={season ? `Season ${season}` : undefined} isLoading={schedulePreviewLoading}>
        {!schedulePreviewLoading && conferenceMissing && (
          <div className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-amber-900">
              No conference is configured for this season ({MISSING_CONFERENCE_LABEL}).
            </p>
            <button
              type="button"
              onClick={navigateToConferenceHistory}
              className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
            >
              Edit in Conference History
            </button>
          </div>
        )}

        {scheduleSoftFilter && Array.isArray(scheduleSoftFilter.warnings) && scheduleSoftFilter.warnings.length > 0 && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-amber-900">Schedule/XML warnings</p>
                <p className="mt-1 text-xs text-amber-800">
                  Matched games: {scheduleSoftFilter.matched_count ?? '—'} · XML kept after filter: {scheduleSoftFilter.keepers_remaining ?? '—'}
                </p>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              {scheduleSoftFilter.warnings.map((w: any, idx: number) => {
                const code = String(w?.code ?? 'warning');
                const message = String(w?.message ?? code);
                const detail = (w && typeof w === 'object') ? (w.detail ?? {}) : {};
                const key = `${code}-${idx}`;

                const isMissingXml = code === 'schedule_row_without_xml';
                return (
                  <div key={key} className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-800">{message}</p>
                        {(detail?.date || detail?.opponent || detail?.score) && (
                          <p className="mt-1 text-xs text-slate-500">
                            {detail?.date ? `Date: ${detail.date}` : ''}
                            {detail?.opponent ? `${detail?.date ? ' · ' : ''}Opponent: ${detail.opponent}` : ''}
                            {detail?.score ? `${(detail?.date || detail?.opponent) ? ' · ' : ''}Score: ${detail.score}` : ''}
                          </p>
                        )}
                        {w?.filename && <p className="mt-1 text-[11px] text-slate-400">File: {String(w.filename)}</p>}
                      </div>

                      {isMissingXml && (
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept=".xml"
                            className="text-xs"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              uploadMissingGameXml({ file, detail, key });
                              // allow selecting same file again later
                              e.currentTarget.value = '';
                            }}
                            disabled={scheduleXmlUploadBusyKey === key}
                            title="Upload the missing game XML for this schedule row"
                          />
                          <span className="text-[11px] text-slate-500">
                            {scheduleXmlUploadBusyKey === key ? 'Uploading…' : 'Upload XML'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              {['#', 'Date', 'Opponent', 'Conference', 'Score', 'Match type', 'Result'].map(h => (
                <th key={h} className="pb-2 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filledScheduleRows.map((row, idx) => {
              const ours = Number(row.ourScore);
              const theirs = Number(row.opponentScore);
              const hasScores = row.ourScore !== '' && row.opponentScore !== '';
              const result = hasScores ? (ours > theirs ? 'W' : ours < theirs ? 'L' : 'T') : '—';
              const badge =
                result === 'W' ? 'text-emerald-600 bg-emerald-50 border border-emerald-100' :
                result === 'L' ? 'text-rose-600 bg-rose-50 border border-rose-100' :
                result === 'T' ? 'text-amber-600 bg-amber-50 border border-amber-100' :
                'text-slate-400 bg-slate-50';
              return (
                <tr key={row.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 pr-4 text-slate-400">{idx + 1}</td>
                  <td className="py-3 pr-4 text-slate-700">{row.date || '—'}</td>
                  <td className="py-3 pr-4 font-medium text-slate-800">{row.opponent || '—'}</td>
                  <td className="py-3 pr-4 text-slate-700">{conferenceDisplayLabel}</td>
                  <td className="py-3 pr-4 text-slate-700">{hasScores ? `${row.ourScore} – ${row.opponentScore}` : '—'}</td>
                  <td className="py-3 pr-4 text-slate-700">{row.matchType || '—'}</td>
                  <td className="py-3"><span className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-bold ${badge}`}>{result}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!schedulePreviewLoading && (conferenceConfigured || conferenceMissing) && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={navigateToConferenceHistory}
              className="text-xs font-semibold text-cyan-600 hover:text-cyan-700"
            >
              Edit conference in Conference History
            </button>
          </div>
        )}
      </PreviewModal>

      <PreviewModal isOpen={rosterPreviewOpen} onClose={() => setRosterPreviewOpen(false)} title="Roster Preview" isLoading={rosterPreviewLoading}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              {['#', 'Class', 'Position', 'First Name', 'Family Name'].map(h => (
                <th key={h} className="pb-2 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filledRosterRows.map((row, idx) => (
              <tr key={row.id} className="border-b border-slate-100 last:border-0">
                <td className="py-3 pr-4 text-slate-400">{idx + 1}</td>
                <td className="py-3 pr-4 text-slate-700">{row.classYear || '—'}</td>
                <td className="py-3 pr-4 text-slate-700">{row.position || '—'}</td>
                <td className="py-3 pr-4 font-medium text-slate-800">{row.firstName || '—'}</td>
                <td className="py-3 text-slate-700">{row.familyName || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PreviewModal>

      <PreviewModal isOpen={zipPreviewOpen} onClose={() => setZipPreviewOpen(false)} title="ZIP File Preview" subtitle={xmlZip?.name} isLoading={zipPreviewLoading}>
        <div className="flex flex-col items-center justify-center py-10 gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckIcon className="h-7 w-7 text-emerald-600" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-slate-800">{xmlZip?.name}</p>
            <p className="mt-1 text-sm text-slate-500">{xmlZip ? `${(xmlZip.size / (1024 * 1024)).toFixed(2)} MB` : ''}</p>
            <p className="mt-3 text-sm font-medium text-emerald-600">File validated and data imported for editing.</p>
            <p className="mt-2 text-sm text-slate-500">Schedule rows: {filledScheduleRows.length} · Roster rows: {filledRosterRows.length}</p>
          </div>
        </div>
      </PreviewModal>

      <ErrorModal 
        isOpen={errorModal.isOpen} 
        onClose={() => setErrorModal({ isOpen: false, error: null })} 
        error={errorModal.error} 
      />

      <TeamIdentityMappingModal
        open={teamIdentityModalOpen}
        candidates={teamIdentityCandidates}
        trustedTeamName={collegeNameFromAuth}
        trustedTeamCode={storedUser?.team?.code}
        isSubmitting={isSubmitting}
        onClose={() => setTeamIdentityModalOpen(false)}
        onMap={handleTeamIdentityMapping}
      />

      <UploadAcknowledgementModal
        open={ackModalOpen}
        eligibleCount={ackPayload?.eligible_xmls?.length ?? 0}
        skippedXmls={ackPayload?.skipped_xmls ?? []}
        isSubmitting={isSubmitting}
        onProceed={handleAckProceed}
        onCancel={handleAckCancel}
      />

      <QueuedModal
        isOpen={queuedModalOpen}
        onClose={dismissQueuedModal}
        scheduleSummary={queuedScheduleSummary}
      />

      <CsvPopup isOpen={showCsvPopup} onClose={() => setShowCsvPopup(false)} onUpload={file => console.log('CSV uploaded:', file.name)} />
    </div>
  );
}

export default StatCrewUploadPage;
